// src/components/Certificates.js

import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaExternalLinkAlt } from "react-icons/fa";
import CertificateCard from "./CertificateCard";
import TimelineView from "./TimelineView";
import TimelineToggle from "./TimelineToggle";
import { useTimelineView } from "../hooks/useTimelineView";
import { useData } from "../hooks/useData";
import { getCertificates } from "../firebase/dataService";
import "./Certificates.css";

const SkeletonCard = () => (
  <div className="cert-skeleton">
    <div className="skeleton" style={{ aspectRatio: "4/3" }} />
    <div style={{ padding: "1.25rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <div className="skeleton" style={{ height: "12px", width: "35%" }} />
      <div className="skeleton" style={{ height: "16px", width: "85%" }} />
      <div className="skeleton" style={{ height: "12px" }} />
      <div className="skeleton" style={{ height: "12px", width: "60%" }} />
    </div>
  </div>
);

const CertTimelineCard = ({ title, description, completion_date, verify }) => {
  const preview = (description || "").length > 110
    ? description.slice(0, 110).trimEnd() + "…"
    : description;
  return (
    <>
      <div className="tl-view__card__title">{title}</div>
      {preview && <div className="tl-view__card__meta">{preview}</div>}
      {verify && (
        <a href={verify} target="_blank" rel="noreferrer" className="tl-view__card__verify">
          <FaExternalLinkAlt size={10} /> Verify certificate
        </a>
      )}
    </>
  );
};

const Certificates = ({ limit, title = "Certifications", showAll = false }) => {
  const { data: certificates, loading, error } = useData(getCertificates);
  const [view, setView] = useTimelineView("certs", "grid");

  const displayed = limit ? certificates.slice(0, limit) : certificates;

  return (
    <section className="certs-section">
      <div className="container">
        <div className="certs-section__header">
          <div>
            <p className="section-label">Credentials</p>
            <h2 className="section-title">{title}</h2>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
            <TimelineToggle view={view} onChange={setView} />
            {showAll && (
              <Link to="/certifications" className="btn btn-outline">
                All Certificates <FaArrowRight size={13} />
              </Link>
            )}
          </div>
        </div>

        {error && (
          <p style={{ color: "var(--accent)", textAlign: "center", padding: "2rem" }}>
            Failed to load certificates.
          </p>
        )}

        {loading ? (
          <div className="certs-grid">
            {Array.from({ length: limit || 3 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : view === "timeline" ? (
          <TimelineView
            items={displayed}
            getDate={c => c.completion_date || ""}
            accentColor="#1D9E75"
            renderCard={c => <CertTimelineCard {...c} />}
          />
        ) : (
          <div className="certs-grid">
            {displayed.map(cert => <CertificateCard key={cert.id} {...cert} />)}
          </div>
        )}
      </div>
    </section>
  );
};

export default Certificates;